import React, { useRef } from "react";
import {
  FileText,
  Download,
  Printer,
  X,
  CheckCircle,
  Calendar,
  User,
  Building2,
} from "lucide-react";
import { notifyInfo, notifySuccess } from "../utils/notify";

/**
 * Interface para dados do candidato
 */
interface DadosCandidato {
  nome: string;
  cpf: string;
  setor: string;
  funcao: string;
  dataAdmissao?: string;
  protocolo: string;
  dataRegistro: string;
  horaRegistro: string;
  empresa: string;
  cnpj?: string;
  eleicao?: {
    dataInicio: string;
    dataFim: string;
    mandato: string;
  };
}

/**
 * Props do componente
 */
interface ConfirmacaoCandidaturaModalProps {
  isOpen: boolean;
  onClose: () => void;
  dados: DadosCandidato;
  onPrint?: () => void;
}

/**
 * Modal de confirmação de candidatura com comprovante PDF
 *
 * Este componente exibe um comprovante formal de inscrição como candidato à CIPA.
 * Permite impressão e download do comprovante em PDF.
 *
 * O protocolo gerado pode ser usado posteriormente para:
 * - Confirmar a inscrição
 * - Verificar o status da candidatura
 * - Comprovação junto ao SESMT/RH
 */
const ConfirmacaoCandidaturaModal: React.FC<
  ConfirmacaoCandidaturaModalProps
> = ({ isOpen, onClose, dados, onPrint }) => {
  const comprovanteRef = useRef<HTMLDivElement>(null);

  /**
   * Mascara CPF para exibição (privacidade)
   */
  const mascararCpf = (cpf: string): string => {
    const cpfLimpo = cpf.replace(/\D/g, "");
    if (cpfLimpo.length !== 11) return cpf;
    return `***.${cpfLimpo.substring(3, 6)}.${cpfLimpo.substring(6, 9)}-**`;
  };

  /**
   * Formata data para exibição
   */
  const formatarData = (dataStr: string): string => {
    if (!dataStr) return "-";
    const data = new Date(dataStr + "T12:00:00");
    return data.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  /**
   * Imprime o comprovante usando a API de impressão do navegador
   */
  const imprimirComprovante = () => {
    const conteudo = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Comprovante de Candidatura CIPA - ${dados.protocolo}</title>
    <style>
        @page {
            size: A4;
            margin: 2cm;
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            font-size: 12pt;
            line-height: 1.6;
            color: #333;
            background: #fff;
        }
        
        .container {
            max-width: 21cm;
            margin: 0 auto;
            padding: 20px;
        }
        
        .header {
            text-align: center;
            border-bottom: 3px double #2e7d32;
            padding-bottom: 20px;
            margin-bottom: 20px;
        }
        
        .header h1 {
            color: #2e7d32;
            font-size: 18pt;
            margin-bottom: 5px;
        }
        
        .header h2 {
            font-size: 14pt;
            color: #666;
            font-weight: normal;
        }
        
        .empresa-info {
            text-align: center;
            margin-bottom: 15px;
            font-size: 11pt;
            color: #555;
        }
        
        .protocolo-box {
            background: #e8f5e9;
            border: 2px solid #4caf50;
            border-radius: 8px;
            padding: 15px;
            text-align: center;
            margin: 20px 0;
        }
        
        .protocolo-box .label {
            font-size: 10pt;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 5px;
        }
        
        .protocolo-box .numero {
            font-size: 18pt;
            font-weight: bold;
            color: #2e7d32;
            font-family: 'Courier New', monospace;
            letter-spacing: 2px;
        }
        
        .dados-section {
            margin: 20px 0;
        }
        
        .dados-section h3 {
            color: #2e7d32;
            font-size: 12pt;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 5px;
            margin-bottom: 10px;
        }
        
        .dados-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
        }
        
        .dado-item {
            margin-bottom: 8px;
        }
        
        .dado-item .label {
            font-size: 9pt;
            color: #888;
            text-transform: uppercase;
        }
        
        .dado-item .valor {
            font-size: 11pt;
            color: #333;
            font-weight: 500;
        }
        
        .aviso-box {
            background: #fff3e0;
            border-left: 4px solid #ff9800;
            padding: 12px 15px;
            margin: 20px 0;
            font-size: 10pt;
        }
        
        .aviso-box .titulo {
            font-weight: bold;
            color: #e65100;
            margin-bottom: 5px;
        }
        
        .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #e0e0e0;
            font-size: 9pt;
            color: #888;
            text-align: center;
        }
        
        .footer .data-emissao {
            margin-bottom: 10px;
        }
        
        .qrcode-placeholder {
            width: 80px;
            height: 80px;
            border: 1px dashed #ccc;
            margin: 10px auto;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8pt;
            color: #aaa;
        }
        
        .assinatura-area {
            display: flex;
            justify-content: space-between;
            margin-top: 40px;
            padding-top: 20px;
        }
        
        .assinatura-linha {
            width: 200px;
            text-align: center;
        }
        
        .assinatura-linha .linha {
            border-top: 1px solid #333;
            margin-bottom: 5px;
        }
        
        .assinatura-linha .nome {
            font-size: 10pt;
        }
        
        @media print {
            body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
            }
            
            .no-print {
                display: none !important;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>COMPROVANTE DE INSCRIÇÃO</h1>
            <h2>Processo Eleitoral CIPA</h2>
        </div>
        
        <div class="empresa-info">
            <strong>${dados.empresa}</strong>
            ${dados.cnpj ? `<br>CNPJ: ${dados.cnpj}` : ""}
        </div>
        
        <div class="protocolo-box">
            <div class="label">Protocolo de Inscrição</div>
            <div class="numero">${dados.protocolo}</div>
        </div>
        
        <div class="dados-section">
            <h3>Dados do Candidato</h3>
            <div class="dados-grid">
                <div class="dado-item">
                    <div class="label">Nome Completo</div>
                    <div class="valor">${dados.nome}</div>
                </div>
                <div class="dado-item">
                    <div class="label">CPF</div>
                    <div class="valor">${mascararCpf(dados.cpf)}</div>
                </div>
                <div class="dado-item">
                    <div class="label">Setor</div>
                    <div class="valor">${dados.setor}</div>
                </div>
                <div class="dado-item">
                    <div class="label">Função</div>
                    <div class="valor">${dados.funcao}</div>
                </div>
                ${
                  dados.dataAdmissao
                    ? `
                <div class="dado-item">
                    <div class="label">Data de Admissão</div>
                    <div class="valor">${formatarData(dados.dataAdmissao)}</div>
                </div>
                `
                    : ""
                }
            </div>
        </div>
        
        <div class="dados-section">
            <h3>Dados do Registro</h3>
            <div class="dados-grid">
                <div class="dado-item">
                    <div class="label">Data do Registro</div>
                    <div class="valor">${formatarData(dados.dataRegistro)}</div>
                </div>
                <div class="dado-item">
                    <div class="label">Hora do Registro</div>
                    <div class="valor">${dados.horaRegistro}</div>
                </div>
                ${
                  dados.eleicao
                    ? `
                <div class="dado-item">
                    <div class="label">Período de Votação</div>
                    <div class="valor">${formatarData(dados.eleicao.dataInicio)} a ${formatarData(dados.eleicao.dataFim)}</div>
                </div>
                <div class="dado-item">
                    <div class="label">Mandato</div>
                    <div class="valor">${dados.eleicao.mandato}</div>
                </div>
                `
                    : ""
                }
            </div>
        </div>
        
        <div class="aviso-box">
            <div class="titulo">⚠️ IMPORTANTE</div>
            <p>
                Este documento comprova sua inscrição como candidato(a) à CIPA.
                Guarde este comprovante para eventual verificação.
                A candidatura está sujeita à validação pela Comissão Eleitoral.
            </p>
        </div>
        
        <div class="assinatura-area">
            <div class="assinatura-linha">
                <div class="linha"></div>
                <div class="nome">Candidato(a)</div>
            </div>
            <div class="assinatura-linha">
                <div class="linha"></div>
                <div class="nome">Comissão Eleitoral</div>
            </div>
        </div>
        
        <div class="footer">
            <div class="data-emissao">
                Documento gerado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}
            </div>
            <p>
                Sistema CIPA Fácil - Gestão Completa de CIPA<br>
                <small>Este documento tem validade apenas para fins de comprovação de inscrição no processo eleitoral.</small>
            </p>
        </div>
    </div>
</body>
</html>
    `;

    const janelaImpressao = window.open("", "_blank");
    if (janelaImpressao) {
      janelaImpressao.document.write(conteudo);
      janelaImpressao.document.close();
      janelaImpressao.focus();

      // Aguardar carregamento antes de imprimir
      setTimeout(() => {
        janelaImpressao.print();
      }, 250);
    } else {
      notifyInfo("Permita pop-ups para imprimir o comprovante.");
    }

    if (onPrint) onPrint();
  };

  /**
   * Faz download do comprovante como arquivo HTML
   * Nota: Para PDF nativo, seria necessário adicionar jsPDF ao projeto
   */
  const downloadComprovante = () => {
    // Criar conteúdo HTML simplificado
    const conteudo = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <title>Comprovante de Candidatura - ${dados.protocolo}</title>
    <style>
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; }
        .header { text-align: center; border-bottom: 2px solid #2e7d32; padding-bottom: 20px; margin-bottom: 20px; }
        .protocolo { background: #e8f5e9; padding: 15px; text-align: center; border-radius: 8px; margin: 20px 0; }
        .protocolo .numero { font-size: 24px; font-weight: bold; color: #2e7d32; font-family: monospace; }
        .dados { margin: 20px 0; }
        .dados h3 { color: #2e7d32; border-bottom: 1px solid #eee; padding-bottom: 5px; }
        .dado { margin: 10px 0; }
        .dado .label { color: #888; font-size: 12px; }
        .dado .valor { font-weight: 500; }
        .aviso { background: #fff3e0; border-left: 4px solid #ff9800; padding: 15px; margin: 20px 0; }
        .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; border-top: 1px solid #eee; padding-top: 20px; }
    </style>
</head>
<body>
    <div class="header">
        <h1>COMPROVANTE DE INSCRIÇÃO - CIPA</h1>
        <h2>${dados.empresa}</h2>
    </div>
    
    <div class="protocolo">
        <div style="font-size: 12px; color: #666;">PROTOCOLO DE INSCRIÇÃO</div>
        <div class="numero">${dados.protocolo}</div>
    </div>
    
    <div class="dados">
        <h3>Dados do Candidato</h3>
        <div class="dado">
            <div class="label">NOME COMPLETO</div>
            <div class="valor">${dados.nome}</div>
        </div>
        <div class="dado">
            <div class="label">CPF</div>
            <div class="valor">${mascararCpf(dados.cpf)}</div>
        </div>
        <div class="dado">
            <div class="label">SETOR / FUNÇÃO</div>
            <div class="valor">${dados.setor} / ${dados.funcao}</div>
        </div>
    </div>
    
    <div class="dados">
        <h3>Dados do Registro</h3>
        <div class="dado">
            <div class="label">DATA E HORA DO REGISTRO</div>
            <div class="valor">${formatarData(dados.dataRegistro)} às ${dados.horaRegistro}</div>
        </div>
    </div>
    
    <div class="aviso">
        <strong>⚠️ IMPORTANTE:</strong> Este documento comprova sua inscrição como candidato(a) à CIPA.
        Guarde este comprovante para eventual verificação.
    </div>
    
    <div class="footer">
        <p>Documento gerado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
        <p>Sistema CIPA Fácil - Gestão Completa de CIPA</p>
    </div>
</body>
</html>
    `;

    // Criar blob e fazer download
    const blob = new Blob([conteudo], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `comprovante-candidatura-${dados.protocolo}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notifySuccess(
      'Comprovante salvo! Abra o arquivo e use "Salvar como PDF" no navegador para converter.',
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Candidatura Registrada!</h2>
                <p className="text-green-100 text-sm">
                  Sua inscrição foi realizada com sucesso
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" ref={comprovanteRef}>
          {/* Protocolo */}
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center mb-6">
            <p className="text-sm text-green-600 font-medium uppercase tracking-wide mb-2">
              Protocolo de Inscrição
            </p>
            <p className="text-3xl font-bold text-green-700 font-mono tracking-widest">
              {dados.protocolo}
            </p>
            <p className="text-xs text-green-500 mt-2">
              Guarde este número para consultas futuras
            </p>
          </div>

          {/* Dados do candidato */}
          <div className="mb-6">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
              <User className="w-5 h-5 text-green-600" />
              Dados do Candidato
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Nome Completo
                  </p>
                  <p className="text-gray-900 font-medium">{dados.nome}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    CPF
                  </p>
                  <p className="text-gray-900 font-medium">
                    {mascararCpf(dados.cpf)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Setor
                  </p>
                  <p className="text-gray-900 font-medium">{dados.setor}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Função
                  </p>
                  <p className="text-gray-900 font-medium">{dados.funcao}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dados da empresa */}
          <div className="mb-6">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
              <Building2 className="w-5 h-5 text-green-600" />
              Empresa
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-900 font-medium">{dados.empresa}</p>
              {dados.cnpj && (
                <p className="text-sm text-gray-500">CNPJ: {dados.cnpj}</p>
              )}
            </div>
          </div>

          {/* Dados do registro */}
          <div className="mb-6">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
              <Calendar className="w-5 h-5 text-green-600" />
              Dados do Registro
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Data
                  </p>
                  <p className="text-gray-900 font-medium">
                    {formatarData(dados.dataRegistro)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Hora
                  </p>
                  <p className="text-gray-900 font-medium">
                    {dados.horaRegistro}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Informações da eleição */}
          {dados.eleicao && (
            <div className="mb-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                <FileText className="w-5 h-5 text-green-600" />
                Informações da Eleição
              </h3>
              <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Período de Votação:
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatarData(dados.eleicao.dataInicio)} a{" "}
                    {formatarData(dados.eleicao.dataFim)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Mandato:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {dados.eleicao.mandato}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Aviso importante */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 text-lg">⚠️</span>
              </div>
              <div>
                <p className="font-medium text-amber-800 mb-1">Importante</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Guarde este comprovante para consultas futuras</li>
                  <li>
                    • Sua candidatura será validada pela Comissão Eleitoral
                  </li>
                  <li>
                    • Acompanhe o período de votação e divulgue sua candidatura
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer com ações */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            Sistema CIPA Fácil - Gestão Completa de CIPA
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={downloadComprovante}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Salvar
            </button>
            <button
              onClick={imprimirComprovante}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              Imprimir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmacaoCandidaturaModal;
